'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const galleryImages = [
  { src: 'https://da1mambadetailing.com/assets/97126918-EC29-40BB-B6B4-2F58B077FB33_1751754684931.jpg', alt: 'Tesla sunset foam wash premium detailing service' },
  { src: 'https://da1mambadetailing.com/assets/64745EE9-DA2F-493A-B030-5BB30C3F9EA9_1751754690948.jpg', alt: 'Pristine Lamborghini after professional detailing' },
  { src: 'https://da1mambadetailing.com/assets/IMG_0296_1751754717613.jpg', alt: 'BMW M5 and Rolls-Royce fleet luxury detailing service' },
  { src: 'https://da1mambadetailing.com/assets/280047261_531716588413514_7693021305676078627_n_1751758254505.jpg', alt: 'Lamborghini paint correction masking preparation' },
  { src: 'https://da1mambadetailing.com/assets/280216952_1462596080837580_1062062813163839722_n_1751758262081.jpg', alt: 'Lamborghini flawless paint correction results' },
  { src: 'https://da1mambadetailing.com/assets/314609221_166020389450136_2725712082903081790_n_1751758475934.jpg', alt: 'Rolls Royce professional luxury detailing service' },
];

const services = [
  {
    title: 'DA1MAMBA Detail',
    description: 'Complete interior + exterior reset. Shampoo & steam, clay bar decon, gloss protection, brand-new microfiber towels every vehicle.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    ),
  },
  {
    title: 'Paint Correction',
    description: 'Remove swirls, haze & light scratches safely. Dealership-grade correction trusted on Ferrari, Lamborghini, Porsche, and Tesla.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
  },
  {
    title: 'Ceramic Coating',
    description: 'Long-term hydrophobic barrier. Deep glass-like gloss, UV protection, and easier washing. Entry-level 6-month & premium 2+ year options.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
      </svg>
    ),
  },
  {
    title: 'Full Exterior Detail',
    description: 'Touchless pre-rinse, foam bath, clay bar decon, hand wash, trim dressing, premium sealant or spray ceramic topper.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
      </svg>
    ),
  },
  {
    title: 'Full Interior Detail',
    description: 'Precision vacuum, shampoo & steam, leather clean & condition, panels/vents/buttons detailed, streak-free glass & sanitization.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
  },
  {
    title: 'Boat Detailing',
    description: 'Hull cleaning, deck washing, interior cabin detailing, upholstery cleaning and protection, metal and chrome polishing.',
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
      </svg>
    ),
  },
];

const trustItems = [
  { label: 'We Come To You', icon: '🚗' },
  { label: 'Premium-Grade Products', icon: '✦' },
  { label: 'Zero Scratch Risk', icon: '🛡' },
  { label: 'Obsessive Attention to Detail', icon: '◈' },
  { label: 'Orange County\'s #1 Mobile Detail', icon: '📍' },
];

const testimonials = [
  {
    quote: 'My Porsche 911 looked better than the day I bought it. These guys are the real deal — not a single swirl mark, not a single missed spot.',
    name: 'Michael Rodriguez',
    location: 'Newport Beach, CA',
    initials: 'MR',
  },
  {
    quote: 'I\'ve tried four other detailers. DA1 MAMBA is in a different league. My Range Rover looked showroom-fresh and they came right to my office.',
    name: 'Jennifer Chen',
    location: 'Irvine, CA',
    initials: 'JC',
  },
  {
    quote: 'I trust DA1 MAMBA with my entire collection. Consistent, careful, and they actually care about the result. That\'s rare.',
    name: 'David Kim',
    location: 'Orange County, CA',
    initials: 'DK',
  },
];

const cities = [
  'Newport Beach', 'Irvine', 'Laguna Beach', 'Laguna Niguel', 'Dana Point',
  'Mission Viejo', 'Lake Forest', 'Tustin', 'Anaheim', 'Orange',
  'Santa Ana', 'Costa Mesa', 'Garden Grove', 'Huntington Beach',
];

export default function HomePage() {
  const [heroLoaded, setHeroLoaded] = useState(false);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <Header />

      {/* HERO */}
      <section className="relative min-h-screen flex flex-col overflow-hidden">
        <div className="absolute inset-0 z-0">
          <Image
            src="https://da1mambadetailing.com/assets/ChatGPT%20Image%20Jul%205,%202025%20at%2012_15_29%20PM_1751743184967.png"
            alt="Rolls Royce Ghost luxury car receiving premium detailing service"
            fill
            className={`object-cover object-center transition-opacity duration-700 ${heroLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setHeroLoaded(true)}
            priority
            unoptimized
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0a0a0a]" />
        </div>

        {/* Main hero content */}
        <div className="relative z-10 flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-24 pb-8">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-[#B8860B] text-xs font-semibold tracking-widest uppercase mb-6">
              Orange County's Premier Mobile Detailing
            </p>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Your car deserves better<br className="hidden sm:block" /> than a basic wash
            </h1>
            <p className="text-[#D1D5DB] text-base sm:text-xl max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed">
              We come to you. Premium mobile detailing, paint correction, and ceramic coating — delivered to your home or office across Newport Beach, Irvine, and all of Orange County.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8 sm:mb-10">
              <a
                href="sms:9492935721"
                className="w-full sm:w-auto bg-[#4B0082] hover:bg-[#6B21A8] text-white font-semibold px-8 py-4 rounded text-sm tracking-wide uppercase transition-colors duration-200"
              >
                Text for a Free Quote
              </a>
              <a
                href="tel:+19492935721"
                className="w-full sm:w-auto border border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-black font-semibold px-8 py-4 rounded text-sm tracking-wide uppercase transition-colors duration-200"
              >
                Call Now
              </a>
              <Link
                href="/gallery"
                className="w-full sm:w-auto border border-white/20 text-[#D1D5DB] hover:border-white hover:text-white font-semibold px-8 py-4 rounded text-sm tracking-wide uppercase transition-colors duration-200 flex items-center justify-center gap-1"
              >
                See the Results
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>

            <p className="text-[#9CA3AF] text-xs tracking-wide">
              Trusted by Porsche, Ferrari, Lamborghini & Tesla owners across Orange County
            </p>
          </div>
        </div>

        {/* Bottom bar — social links */}
        <div className="relative z-10 px-4 sm:px-6 lg:px-8 pb-6">
          <div className="max-w-7xl mx-auto">
            <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded px-4 py-3">
              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm">
                <a href="tel:+19492935721" className="flex items-center gap-1.5 text-[#D1D5DB] hover:text-[#B8860B] transition-colors duration-200">
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                  </svg>
                  <span className="text-xs sm:text-sm">(949) 293-5721</span>
                </a>
                <a href="https://www.instagram.com/da1mamba/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[#D1D5DB] hover:text-[#B8860B] transition-colors duration-200">
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.014 3.584-.072 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.057 1.645-.069 4.948-.069 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  <span className="text-xs sm:text-sm">@da1mamba</span>
                </a>
                <a href="https://www.youtube.com/@da1mamba" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[#D1D5DB] hover:text-[#B8860B] transition-colors duration-200">
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  <span className="text-xs sm:text-sm">YouTube</span>
                </a>
                <a href="https://www.tiktok.com/@da1mamba" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[#D1D5DB] hover:text-[#B8860B] transition-colors duration-200">
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                  </svg>
                  <span className="text-xs sm:text-sm">TikTok</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO CONTENT SECTION */}
      <section className="bg-[#111111] py-10 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-xl sm:text-3xl font-bold text-[#D4AF37] mb-4 sm:mb-6">Orange County's Most Trusted Mobile Car Detailing</h2>
          <p className="text-white text-sm sm:text-base mb-4 leading-relaxed">
            Da1Mamba Professional Detailing brings luxury-grade mobile car detailing directly to you — serving Newport Beach, Irvine, and all of Orange County. We specialize in premium services for daily drivers, sports cars, exotic vehicles, supercars, classic cars, motorcycles, and boats. Every vehicle is detailed using brand-new microfiber towels, safe chemicals, and zero cross-contamination for the cleanest, scratch-free results.
          </p>
          <p className="text-white text-sm sm:text-base mb-4 leading-relaxed">
            Whether you drive a Tesla, BMW, Mercedes, Ferrari, Lamborghini, Porsche, McLaren, Audi, Truck, SUV, or boat — Da1Mamba is the mobile detailing service Orange County's most discerning owners trust for spotless, showroom-quality results.
          </p>
          <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-[#D4AF37]/30">
            <p className="text-[#D4AF37] font-semibold text-sm sm:text-base mb-2">Service Areas:</p>
            <p className="text-white text-xs sm:text-sm leading-relaxed">
              Newport Beach, Irvine, Laguna Beach, Laguna Niguel, Dana Point, Mission Viejo, Lake Forest, Tustin, Anaheim, Orange, Santa Ana, Costa Mesa, Garden Grove, Huntington Beach, and all surrounding Orange County cities.
            </p>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-[#0a0a0a] border-y border-[#2a2a2a] py-5 sm:py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 lg:gap-12">
            {trustItems?.map((item) => (
              <div key={item?.label} className="flex items-center gap-2">
                <span className="text-[#4B0082] text-lg">{item?.icon}</span>
                <span className="text-[#D1D5DB] text-xs sm:text-sm font-medium tracking-wide">{item?.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="py-12 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <p className="text-[#B8860B] text-xs font-semibold tracking-widest uppercase mb-3">What We Offer</p>
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-4">Services Built for Owners Who Care</h2>
            <p className="text-[#9CA3AF] max-w-xl mx-auto text-sm sm:text-base">
              Every service uses premium products, fresh microfiber towels, and a zero cross-contamination process — because your paint deserves better than shortcuts.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {services?.map((service) => (
              <div
                key={service?.title}
                className="bg-[#1a1a1a] border border-[#2a2a2a] border-l-2 border-l-[#4B0082] p-5 sm:p-6 hover:border-[#4B0082] transition-colors duration-300 group"
              >
                <div className="text-[#4B0082] mb-4 group-hover:text-[#B8860B] transition-colors duration-200">
                  {service?.icon}
                </div>
                <h3 className="text-white font-semibold text-base sm:text-lg mb-3">{service?.title}</h3>
                <p className="text-[#9CA3AF] text-sm leading-relaxed mb-5">{service?.description}</p>
                <a
                  href="sms:9492935721"
                  className="inline-flex items-center gap-1.5 border border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-black font-semibold px-8 py-4 rounded text-sm tracking-wide uppercase transition-colors duration-200"
                >
                  Get a Quote
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            ))}
          </div>

          <div className="text-center mt-8 sm:mt-10">
            <Link
              href="/services"
              className="inline-flex items-center gap-2 border border-[#4B0082] text-[#D1D5DB] hover:bg-[#4B0082] hover:text-white px-8 py-4 rounded text-sm font-semibold tracking-wide uppercase transition-colors duration-200"
            >
              See All Services & Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-12 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#111111]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-16 items-center">
            <div>
              <p className="text-[#B8860B] text-xs font-semibold tracking-widest uppercase mb-3">Why DA1MAMBA</p>
              <h2 className="text-2xl sm:text-4xl font-bold text-white mb-4 sm:mb-6">
                We come to you.<br /> No drop-off. No waiting.
              </h2>
              <p className="text-[#9CA3AF] mb-6 sm:mb-10 leading-relaxed text-sm sm:text-base">
                Da1Mamba brings dealership-grade detailing directly to your driveway or office. No wasted trips, no waiting rooms — just a spotless vehicle waiting for you when we're done.
              </p>
              <ul className="space-y-3 sm:space-y-4">
                {[
                  'Mobile service to your home or office — we come to you',
                  'Premium finish, not rushed volume work',
                  'Built for owners who expect better than average results',
                  'Careful process for paint, trim, glass, wheels, and interiors',
                  'Trusted on Ferrari, Lamborghini, Porsche, Tesla, and more',
                  'Brand-new microfiber towels every vehicle — zero cross-contamination',
                ]?.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-[#4B0082] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-[#D1D5DB] text-sm leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative aspect-[4/3] rounded overflow-hidden">
              <Image
                src="https://da1mambadetailing.com/assets/416006343_685055570326614_4483350650480443101_n_1751763806956.jpg"
                alt="Professional Mercedes-Benz interior detailing by DA1MAMBA"
                fill
                className="object-cover"
                unoptimized
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="py-12 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <p className="text-[#B8860B] text-xs font-semibold tracking-widest uppercase mb-3">Our Work</p>
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-4">The results speak for themselves</h2>
            <p className="text-[#9CA3AF] max-w-xl mx-auto text-sm sm:text-base">
              From daily drivers to six-figure exotics — every vehicle gets the same obsessive level of care.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
            {galleryImages?.map((img, i) => (
              <div key={i} className="relative aspect-square overflow-hidden group">
                <Image
                  src={img?.src}
                  alt={img?.alt}
                  fill
                  className="object-cover transition-transform duration-500 group-hover:scale-105"
                  unoptimized
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300" />
              </div>
            ))}
          </div>

          <div className="text-center mt-8 sm:mt-10">
            <Link
              href="/gallery"
              className="inline-flex items-center gap-2 bg-[#4B0082] hover:bg-[#6B21A8] text-white px-8 py-4 rounded text-sm font-semibold tracking-wide uppercase transition-colors duration-200"
            >
              View Full Gallery
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section className="py-12 sm:py-24 px-4 sm:px-6 lg:px-8 bg-[#111111]">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-[#B8860B] text-xs font-semibold tracking-widest uppercase mb-3">Where We Serve</p>
          <h2 className="text-2xl sm:text-4xl font-bold text-white mb-4">We Come to You — Anywhere in Orange County</h2>
          <p className="text-[#9CA3AF] max-w-xl mx-auto mb-8 sm:mb-12 text-sm sm:text-base">
            No drop-off required. We bring the full detail experience directly to your home, office, or wherever your vehicle is parked.
          </p>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {cities?.map((city) => (
              <span
                key={city}
                className="border border-[#2a2a2a] text-[#D1D5DB] text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 hover:border-[#4B0082] hover:text-white transition-colors duration-200"
              >
                {city}
              </span>
            ))}
            <span className="border border-[#4B0082] text-[#B8860B] text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2">
              + All Surrounding OC Cities
            </span>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-12 sm:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <p className="text-[#B8860B] text-xs font-semibold tracking-widest uppercase mb-3">Client Reviews</p>
            <h2 className="text-2xl sm:text-4xl font-bold text-white mb-4">Don't take our word for it</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {testimonials?.map((t, i) => (
              <div key={i} className="bg-[#1a1a1a] border border-[#2a2a2a] p-6 sm:p-8">
                <div className="flex gap-1 mb-4 sm:mb-6">
                  {[...Array(5)]?.map((_, s) => (
                    <svg key={s} className="w-4 h-4 text-[#B8860B]" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-[#D1D5DB] text-sm leading-relaxed mb-4 sm:mb-6 italic">&ldquo;{t?.quote}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#4B0082] flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">{t?.initials}</span>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{t?.name}</p>
                    <p className="text-[#9CA3AF] text-xs mt-1">{t?.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-12 sm:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1a0030] via-[#2d0050] to-[#0a0a0a]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[#B8860B] text-xs font-semibold tracking-widest uppercase mb-4">Ready to Book?</p>
          <h2 className="text-2xl sm:text-4xl font-bold text-white mb-4">Your vehicle deserves this. Book today.</h2>
          <p className="text-[#D1D5DB] mb-8 sm:mb-10 leading-relaxed text-sm sm:text-base">
            Text or call for a fast, no-pressure quote. We'll come to you — same week availability across Orange County.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <a
              href="sms:9492935721"
              className="w-full sm:w-auto bg-[#4B0082] hover:bg-[#6B21A8] text-white font-semibold px-8 py-4 rounded text-sm tracking-wide uppercase transition-colors duration-200"
            >
              Text for a Free Quote
            </a>
            <a
              href="tel:+19492935721"
              className="w-full sm:w-auto border border-[#B8860B] text-[#B8860B] hover:bg-[#B8860B] hover:text-black font-semibold px-8 py-4 rounded text-sm tracking-wide uppercase transition-colors duration-200"
            >
              Call (949) 293-5721
            </a>
          </div>
          <p className="text-[#9CA3AF] text-xs mt-6 sm:mt-8">Serving Newport Beach, Irvine, and all of Orange County.</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
